import {
    useState
} from "react";
import {
    View
} from "react-native";
import type ContentTabType from "./type";
import stylesheet from "./stylesheet";
import BoardMembersView from "./views/boardMembers";
import GenericInfoView from "./views/genericInfo";
import OldPresidentsView from "./views/oldPresidents";
import PresidentView from "./views/president";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Button,
    Text
} from "ncore-ui-kit";
import {
    GraduationCapIcon,
    FileTextIcon,
    UsersIcon,
    UserIcon
} from "lucide-react-native";

type TabConfig = {
    icon: typeof UserIcon;
    key: ContentTabType;
    titleKey: string;
};

const TABS: TabConfig[] = [
    {
        titleKey: "content-tab-old-presidents",
        icon: GraduationCapIcon,
        key: "oldPresidents"
    },
    {
        titleKey: "content-tab-board-members",
        key: "boardMembers",
        icon: UsersIcon
    },
    {
        titleKey: "content-tab-generic-info",
        icon: FileTextIcon,
        key: "genericInfo"
    },
    {
        titleKey: "content-tab-president",
        key: "president",
        icon: UserIcon
    }
];

const ContentPage = () => {
    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        activeTab,
        setActiveTab
    ] = useState<ContentTabType>("president");

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.titleContainer,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("content-management-title")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {localize("content-management-description")}
                </Text>
            </View>
        </View>;
    };

    const renderTabBar = () => {
        return <View
            style={[
                stylesheet.tabBarContainer,
                {
                    marginBottom: spaces.spacingXl,
                    gap: spaces.spacingSm
                }
            ]}
        >
            {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                const IconComponent = tab.icon;

                return <Button
                    icon={({
                        color,
                        size
                    }) => <IconComponent
                        color={isActive ? colors.project.brandWhite : colors.content.icon[color]}
                        size={size}
                    />}
                    customTextColor={isActive ? colors.project.brandWhite : undefined}
                    variant={isActive ? "filled" : "ghost"}
                    type={isActive ? "primary" : "neutral"}
                    title={localize(tab.titleKey as never)}
                    onPress={() => setActiveTab(tab.key)}
                    spreadBehaviour="free"
                    key={tab.key}
                />;
            })}
        </View>;
    };

    const renderActiveTabContent = () => {
        switch (activeTab) {
            case "boardMembers":
                return <BoardMembersView
                    key="boardMembers"
                />;
            case "oldPresidents":
                return <OldPresidentsView
                    key="oldPresidents"
                />;
            case "genericInfo":
                return <GenericInfoView
                    key="genericInfo"
                />;
            case "president":
            default:
                return <PresidentView
                    key="president"
                />;
        }
    };

    return <PageContainer
        scrollViewStyle={stylesheet.container}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.pageContent,
                {
                    padding: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            {renderHeader()}
            {renderTabBar()}
            <View
                style={stylesheet.tabContentContainer}
            >
                {renderActiveTabContent()}
            </View>
        </View>
    </PageContainer>;
};
export default ContentPage;
